import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Trophy, Users, ChevronDown, ChevronUp, Shield, Loader2, Calendar as CalendarIcon, Swords, KeyRound, Check, ArrowLeft, Flame, Clock, Crown } from 'lucide-react';
import ClubNameLabel from '../components/ClubNameLabel';
import { Button } from '../components/ui/button';
import KnockoutBracket from '../components/KnockoutBracket';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { championshipsApi } from '../lib/api';

// Use current window origin (matches ept-italy.it when deployed, preview URL
// when in dev). This avoids issues where REACT_APP_BACKEND_URL is baked into
// the bundle at build time pointing at a stale backend host.
const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const Tornei = () => {
  const { i18n } = useTranslation();
  const isItalian = i18n.language === 'it';
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCaptain = !!(user && (user.role === 'captain' || user.role === 'admin' || user.role === 'super_admin'));
  const [championships, setChampionships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedChamp, setExpandedChamp] = useState(null);
  const [standingsData, setStandingsData] = useState({});
  const [loadingStandings, setLoadingStandings] = useState({});
  const [matchesData, setMatchesData] = useState({});
  const [showSchedule, setShowSchedule] = useState({});
  const [topScorersByGroup, setTopScorersByGroup] = useState({});
  const [topAssistsByGroup, setTopAssistsByGroup] = useState({});
  const [joinKeyByChamp, setJoinKeyByChamp] = useState({}); // { champId: 'KEY-xxx' }
  const [joiningChamp, setJoiningChamp] = useState(null); // championship_id currently joining

  const handleJoinTournament = async (champ) => {
    const key = (joinKeyByChamp[champ.championship_id] || '').trim();
    if (!key) {
      toast.error(isItalian ? 'Inserisci la chiave d\'accesso' : 'Enter the access key');
      return;
    }
    setJoiningChamp(champ.championship_id);
    try {
      await championshipsApi.join(champ.championship_id, key);
      // Compose a friendly "hype" toast with the live progress
      const total = champ.max_teams || 0;
      const nowCount = (champ.clubs?.length || 0) + 1;
      const remaining = Math.max(0, total - nowCount);
      if (remaining === 0) {
        toast.success(isItalian
          ? `🎉 ${nowCount}/${total} club iscritti — Torneo AL COMPLETO! Inizierà a breve.`
          : `🎉 ${nowCount}/${total} clubs registered — Tournament FULL! Starting soon.`);
      } else {
        toast.success(isItalian
          ? `✅ Iscrizione confermata! 📊 ${nowCount}/${total} club iscritti — mancano ${remaining} squadre per iniziare.`
          : `✅ Registered! 📊 ${nowCount}/${total} clubs — ${remaining} team${remaining === 1 ? '' : 's'} missing to start.`,
          { duration: 6000 });
      }
      setJoinKeyByChamp(prev => ({ ...prev, [champ.championship_id]: '' }));
      fetchChampionships();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || (isItalian ? 'Errore iscrizione' : 'Registration error');
      toast.error(msg);
    } finally {
      setJoiningChamp(null);
    }
  };

  useEffect(() => {
    fetchChampionships();
  }, []);

  // Auto-expand a tournament when the URL has `?expand=<champ_id>` (used after
  // generating the knockout phase from the Super Admin).
  useEffect(() => {
    if (championships.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const expandId = params.get('expand');
    if (expandId && championships.some(c => c.championship_id === expandId)) {
      setExpandedChamp(expandId);
      fetchStandings(expandId);
      fetchMatches(expandId);
      // Scroll into view after a short delay so the card is rendered
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="tournament-card-${expandId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [championships.length]);

  const fetchChampionships = async () => {
    try {
      const res = await fetch(`${API_URL}/api/championships`);
      const data = await res.json();
      // Filter: show ONLY actual tournaments (multi-group OR knockout OR hybrid)
      // Plain single-group "championships" (Serie A/B) are excluded — they live
      // on their own /championship page.
      const onlyTournaments = (data || []).filter((c) => {
        if (!c) return false;
        return (c.num_groups || 1) > 1
          || c.tournament_type === 'knockout'
          || c.tournament_type === 'groups_with_knockout';
      });
      setChampionships(onlyTournaments);
      // Preload first match date for in-progress tournaments (so header badge can render)
      onlyTournaments
        .filter((c) => c.status === 'in_progress')
        .slice(0, 10)
        .forEach((c) => fetchMatches(c.championship_id));
    } catch (err) {
      console.error('Error fetching championships:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStandings = async (champId) => {
    if (standingsData[champId]) return;
    setLoadingStandings(prev => ({ ...prev, [champId]: true }));
    try {
      const res = await fetch(`${API_URL}/api/championships/${champId}/standings`);
      const data = await res.json();
      setStandingsData(prev => ({ ...prev, [champId]: data }));
    } catch (err) {
      console.error('Error fetching standings:', err);
    } finally {
      setLoadingStandings(prev => ({ ...prev, [champId]: false }));
    }
  };

  const fetchMatches = async (champId) => {
    if (matchesData[champId]) return;
    try {
      const [matchesRes, clubsRes] = await Promise.all([
        fetch(`${API_URL}/api/matches?championship_id=${champId}&limit=500`),
        fetch(`${API_URL}/api/clubs`),
      ]);
      const matchesJson = await matchesRes.json();
      const clubsJson = await clubsRes.json();
      const matchesArr = Array.isArray(matchesJson) ? matchesJson : (matchesJson.matches || []);
      const clubsList = Array.isArray(clubsJson) ? clubsJson : (clubsJson.clubs || []);
      const clubMap = {};
      clubsList.forEach(c => { clubMap[c.club_id] = c; });
      const enriched = matchesArr.map(m => ({
        ...m,
        home_club: m.home_club || clubMap[m.home_club_id] || { name: '?' },
        away_club: m.away_club || clubMap[m.away_club_id] || { name: '?' },
      }));
      setMatchesData(prev => ({ ...prev, [champId]: enriched }));
      // Compute first scheduled match date for header badge
      const scheduled = enriched
        .filter(m => m.match_date && m.status !== 'completed')
        .map(m => new Date(m.match_date).getTime())
        .filter(t => !isNaN(t));
      if (scheduled.length > 0) {
        const firstTs = Math.min(...scheduled);
        setChampionships(prev => prev.map(c =>
          c.championship_id === champId && !c.first_match_date
            ? { ...c, first_match_date: new Date(firstTs).toISOString() }
            : c
        ));
      }
    } catch (err) {
      console.error('Error fetching matches:', err);
    }
  };

  const fetchTopScorersForGroups = async (champId, groups) => {
    if (topScorersByGroup[champId] && topAssistsByGroup[champId]) return;
    try {
      const [scorerResults, assistResults] = await Promise.all([
        Promise.all(groups.map(g => fetch(`${API_URL}/api/championships/${champId}/top-scorers?limit=5&group=${g}`).then(r => r.json()))),
        Promise.all(groups.map(g => fetch(`${API_URL}/api/championships/${champId}/top-assists?limit=5&group=${g}`).then(r => r.json()))),
      ]);
      const scMap = {};
      const asMap = {};
      groups.forEach((g, i) => {
        scMap[g] = scorerResults[i] || [];
        asMap[g] = assistResults[i] || [];
      });
      setTopScorersByGroup(prev => ({ ...prev, [champId]: scMap }));
      setTopAssistsByGroup(prev => ({ ...prev, [champId]: asMap }));
    } catch (err) {
      console.error('Error fetching top scorers/assists:', err);
    }
  };

  const toggleExpand = (champId) => {
    if (expandedChamp === champId) {
      setExpandedChamp(null);
    } else {
      setExpandedChamp(champId);
      fetchStandings(champId);
      fetchMatches(champId);
    }
  };

  const toggleSchedule = (champId) => {
    setShowSchedule(prev => ({ ...prev, [champId]: !prev[champId] }));
  };

  const getStatusBadge = (status) => {
    const config = {
      registration: { label: isItalian ? 'Iscrizioni Aperte' : 'Open', color: 'bg-[#00E676]/20 text-[#00E676] border-[#00E676]/30' },
      in_progress: { label: isItalian ? 'In Corso' : 'In Progress', color: 'bg-[#FFEA00]/20 text-[#FFEA00] border-[#FFEA00]/30' },
      completed: { label: isItalian ? 'Completato' : 'Completed', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
      suspended: { label: isItalian ? 'Sospeso' : 'Suspended', color: 'bg-[#FF1744]/20 text-[#FF1744] border-[#FF1744]/30' },
    };
    const s = config[status] || config.registration;
    return <span className={`px-2.5 py-1 text-xs rounded border font-bold ${s.color}`}>{s.label}</span>;
  };

  const ClubLogo = ({ logo, name, colors, size = 'w-6 h-6' }) => {
    if (logo) {
      return <img src={logo} alt={name} className={`${size} rounded object-cover`} loading="lazy" />;
    }
    const bg = colors?.primary || '#1a1a2e';
    return (
      <div className={`${size} rounded flex items-center justify-center text-[8px] font-bold text-white shrink-0`} style={{ background: bg }}>
        {name?.charAt(0) || '?'}
      </div>
    );
  };

  const StandingsTable = ({ standings, groupName }) => (
    <div className="mt-3">
      {groupName && (
        <p className="text-xs uppercase tracking-wider text-[#00F0FF] font-bold mb-2 flex items-center gap-2">
          <Shield size={12} />
          {isItalian ? `Girone ${groupName}` : `Group ${groupName}`}
        </p>
      )}
      <div className="overflow-x-auto -mx-2 sm:mx-0">
        <table className="w-full text-sm min-w-[520px]">
          <thead>
            <tr className="border-b border-white/10 text-slate-500 text-xs uppercase">
              <th className="text-left py-2 px-2">#</th>
              <th className="text-left py-2 px-2">{isItalian ? 'Squadra' : 'Team'}</th>
              <th className="text-center py-2 px-1">G</th>
              <th className="text-center py-2 px-1">V</th>
              <th className="text-center py-2 px-1">P</th>
              <th className="text-center py-2 px-1">S</th>
              <th className="text-center py-2 px-1">GF</th>
              <th className="text-center py-2 px-1">GS</th>
              <th className="text-center py-2 px-1">DR</th>
              <th className="text-center py-2 px-2 text-[#FFEA00]">PT</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((team, idx) => {
              // Highlight: oro (1°), argento (2°), bronzo (3°)
              const positionClass = idx === 0
                ? 'bg-gradient-to-r from-[#FFD700]/15 to-transparent border-l-2 border-[#FFD700]'
                : idx === 1
                  ? 'bg-gradient-to-r from-[#C0C0C0]/15 to-transparent border-l-2 border-[#C0C0C0]'
                  : idx === 2
                    ? 'bg-gradient-to-r from-[#CD7F32]/15 to-transparent border-l-2 border-[#CD7F32]'
                    : '';
              const positionColor = idx === 0
                ? 'text-[#FFD700]'
                : idx === 1
                  ? 'text-[#C0C0C0]'
                  : idx === 2
                    ? 'text-[#CD7F32]'
                    : 'text-slate-400';
              return (
                <tr key={team.club_id} className={`border-b border-white/5 ${positionClass}`}>
                  <td className={`py-2 px-2 font-mono font-bold ${positionColor}`}>{idx + 1}</td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                      <ClubLogo logo={team.club_logo} name={team.club_name} colors={team.club_colors} />
                      <span className="font-medium text-white" title={team.club_name || ''}>{team.club_name}</span>
                    </div>
                  </td>
                  <td className="text-center py-2 px-1 text-slate-300">{team.played}</td>
                  <td className="text-center py-2 px-1 text-[#00E676]">{team.won}</td>
                  <td className="text-center py-2 px-1 text-slate-300">{team.drawn}</td>
                  <td className="text-center py-2 px-1 text-[#FF1744]">{team.lost}</td>
                  <td className="text-center py-2 px-1 text-slate-300">{team.goals_for}</td>
                  <td className="text-center py-2 px-1 text-slate-300">{team.goals_against}</td>
                  <td className="text-center py-2 px-1 font-mono text-slate-300">
                    {team.goal_difference > 0 ? '+' : ''}{team.goal_difference}
                  </td>
                  <td className="text-center py-2 px-2 font-bold text-[#FFEA00]">{team.points}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {standings.length === 0 && (
        <p className="text-center text-slate-500 py-4 text-sm">
          {isItalian ? 'Nessuna partita giocata' : 'No matches played yet'}
        </p>
      )}
    </div>
  );

  // When standings load for a multi-group tournament, fetch top scorers per group
  useEffect(() => {
    Object.entries(standingsData).forEach(([champId, sd]) => {
      if (sd?.type === 'multi_group' && sd.groups && !topScorersByGroup[champId]) {
        const groups = Object.keys(sd.groups);
        fetchTopScorersForGroups(champId, groups);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standingsData]);

  const formatMatchDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) +
        ' · ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    } catch { return null; }
  };

  const ScheduleByGroup = ({ matches, group, isItalian }) => {
    // Group matches by match_day
    const filtered = group ? matches.filter(m => m.group === group) : matches;
    const byDay = filtered.reduce((acc, m) => {
      const md = m.match_day ?? 0;
      (acc[md] = acc[md] || []).push(m);
      return acc;
    }, {});
    const days = Object.keys(byDay).map(Number).sort((a, b) => a - b);
    if (days.length === 0) {
      return <p className="text-xs text-slate-500 italic">{isItalian ? 'Calendario non ancora disponibile' : 'Schedule not available yet'}</p>;
    }
    return (
      <div className="space-y-3 mt-3">
        {days.map(d => (
          <div key={d} className="bg-[#0a0d15] border border-white/10 rounded p-3">
            <div className="text-[11px] uppercase tracking-wider text-[#FFEA00] font-bold mb-2 flex items-center gap-2">
              <CalendarIcon size={11} />
              {isItalian ? `Giornata ${d}` : `Matchday ${d}`}
            </div>
            <div className="space-y-1.5">
              {byDay[d].map(m => (
                <div key={m.match_id} className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs py-1 border-b border-white/5 last:border-0 min-w-0">
                  <div className="flex-1 flex items-center gap-1.5 sm:gap-2 min-w-0 justify-end">
                    <ClubNameLabel
                      name={m.home_club?.name}
                      className="text-white text-right font-medium"
                    />
                    <ClubLogo logo={m.home_club?.logo} name={m.home_club?.name} colors={m.home_club?.colors} size="w-4 h-4" />
                  </div>
                  <div className="px-1 sm:px-2 shrink-0">
                    {m.status === 'completed' ? (
                      <span className="font-mono font-bold text-white bg-[#050508] px-1.5 sm:px-2 py-0.5 rounded border border-white/10">
                        {m.home_score ?? 0}-{m.away_score ?? 0}
                      </span>
                    ) : (
                      <span className="text-slate-500 text-[10px] sm:text-[11px]">vs</span>
                    )}
                  </div>
                  <div className="flex-1 flex items-center gap-1.5 sm:gap-2 min-w-0">
                    <ClubLogo logo={m.away_club?.logo} name={m.away_club?.name} colors={m.away_club?.colors} size="w-4 h-4" />
                    <ClubNameLabel
                      name={m.away_club?.name}
                      className="text-white font-medium"
                    />
                  </div>
                  {m.match_date && m.status !== 'completed' && (
                    <span className="text-[10px] text-slate-500 shrink-0 hidden md:inline">{formatMatchDate(m.match_date)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen ept-bg-ambient flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen ept-bg-ambient">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#050508]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => (user ? navigate('/dashboard') : navigate('/'))}
              className="text-slate-300 hover:text-white hover:bg-white/5 -ml-2 shrink-0"
              data-testid="back-to-menu-btn"
            >
              <ArrowLeft size={16} className="mr-1" />
              <span className="hidden sm:inline">{isItalian ? 'Torna al Menu' : 'Back to Menu'}</span>
              <span className="sm:hidden">{isItalian ? 'Menu' : 'Back'}</span>
            </Button>
            <div className="w-px h-6 bg-white/10 hidden sm:block" />
            <Trophy size={22} className="text-[#FFEA00] shrink-0" />
            <h1 className="font-heading text-xl uppercase tracking-wider text-white truncate">
              {isItalian ? 'Tornei' : 'Tournaments'}
            </h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <a
              href="/albo-d-oro"
              className="text-xs sm:text-sm text-[#FF00E5] hover:underline whitespace-nowrap flex items-center gap-1"
              data-testid="link-albo-doro"
            >
              <Crown size={12} />
              {isItalian ? "Albo d'Oro" : 'Hall of Fame'}
            </a>
            <a
              href="/classifica"
              className="text-xs sm:text-sm text-[#00F0FF] hover:underline whitespace-nowrap hidden sm:inline"
            >
              {isItalian ? 'Classifica Pubblica' : 'Public Leaderboard'}
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4" data-testid="tornei-page">
        {/* Open-registration CTA banner */}
        {(() => {
          const openTournaments = championships.filter(c => c.status === 'registration');
          if (openTournaments.length === 0) return null;
          const totalSlots = openTournaments.reduce((acc, c) => acc + (c.max_teams || 0), 0);
          const filledSlots = openTournaments.reduce((acc, c) => acc + (c.clubs?.length || 0), 0);
          const remaining = Math.max(0, totalSlots - filledSlots);
          return (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative overflow-hidden rounded-lg border border-[#FFEA00]/30 bg-gradient-to-br from-[#FFEA00]/10 via-[#FF1744]/5 to-[#00E676]/10 p-4 sm:p-5"
              data-testid="open-registrations-cta"
            >
              <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 mt-0.5 sm:mt-0 w-10 h-10 rounded-lg bg-[#FFEA00]/20 flex items-center justify-center">
                    <Trophy size={20} className="text-[#FFEA00]" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-[#FFEA00] font-bold mb-0.5 flex items-center gap-1.5">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" />
                      {isItalian ? 'Iscrizioni aperte' : 'Open registrations'}
                    </p>
                    <p className="font-heading text-base sm:text-lg text-white leading-tight">
                      {openTournaments.length === 1
                        ? (isItalian
                          ? `🏆 1 torneo aperto: ${remaining} ${remaining === 1 ? 'posto disponibile' : 'posti disponibili'}!`
                          : `🏆 1 open tournament: ${remaining} ${remaining === 1 ? 'spot' : 'spots'} left!`)
                        : (isItalian
                          ? `🏆 ${openTournaments.length} tornei aperti: ${remaining} ${remaining === 1 ? 'posto disponibile' : 'posti disponibili'}!`
                          : `🏆 ${openTournaments.length} open tournaments: ${remaining} ${remaining === 1 ? 'spot' : 'spots'} left!`)}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {isItalian
                        ? 'Iscrivi il tuo club inserendo la chiave d\'accesso ricevuta dall\'organizzatore.'
                        : 'Register your club by entering the access key from the tournament organizer.'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const first = openTournaments[0];
                    const el = document.querySelector(`[data-testid="tournament-card-${first.championship_id}"]`);
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      if (expandedChamp !== first.championship_id) toggleExpand(first.championship_id);
                    }
                  }}
                  className="bg-[#FFEA00] hover:bg-[#FFEA00]/80 text-black font-bold uppercase tracking-wider whitespace-nowrap"
                  data-testid="cta-jump-to-open-btn"
                >
                  {isItalian ? 'Iscriviti ora' : 'Join now'}
                </Button>
              </div>
            </motion.div>
          );
        })()}

        {championships.length === 0 ? (
          <div className="text-center py-16">
            <Trophy size={48} className="mx-auto mb-4 text-slate-600" />
            <p className="text-slate-500 text-lg">
              {isItalian ? 'Nessun torneo disponibile' : 'No tournaments available'}
            </p>
          </div>
        ) : (
          championships.map((champ, i) => (
            <motion.div
              key={champ.championship_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-[#0a0d15] border border-white/10 rounded overflow-hidden"
              data-testid={`tournament-card-${champ.championship_id}`}
            >
              {/* Tournament Header */}
              <button
                onClick={() => toggleExpand(champ.championship_id)}
                className="w-full p-3 sm:p-4 flex items-center justify-between gap-2 hover:bg-white/5 transition-colors text-left"
              >
                <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded bg-[#FFEA00]/10 flex items-center justify-center shrink-0">
                    <Trophy size={18} className="text-[#FFEA00]" />
                  </div>
                  <div className="text-left min-w-0 flex-1">
                    <h3 className="font-heading text-sm sm:text-base text-white uppercase tracking-wider truncate">
                      {champ.name}
                    </h3>
                    <div className="flex items-center gap-2 sm:gap-3 mt-1 flex-wrap">
                      <span className="text-xs text-slate-500">{champ.season}</span>
                      {getStatusBadge(champ.status)}
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Users size={11} /> {champ.clubs?.length || 0}/{champ.max_teams}
                      </span>
                      {champ.match_start_time && (
                        <span className="text-xs text-[#FFEA00] flex items-center gap-1 whitespace-nowrap" title={isItalian ? 'Orario partite' : 'Match kickoff'}>
                          <Clock size={11} /> {champ.match_start_time}
                        </span>
                      )}
                      {champ.first_match_date && (
                        <span className="text-xs text-[#00F0FF] flex items-center gap-1 whitespace-nowrap" title={isItalian ? 'Data prima partita' : 'First match date'}>
                          <CalendarIcon size={11} />
                          {isItalian ? 'dal ' : 'from '}
                          {new Date(champ.first_match_date).toLocaleDateString(isItalian ? 'it-IT' : 'en-US', { day: '2-digit', month: 'short' })}
                        </span>
                      )}
                      {(champ.num_groups || 1) > 1 && (
                        <span className="px-2 py-0.5 text-xs bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/20 rounded whitespace-nowrap">
                          {champ.num_groups} {isItalian ? 'Gironi' : 'Groups'}
                        </span>
                      )}
                      {champ.tournament_type === 'knockout' && (
                        <span className="px-2 py-0.5 text-xs bg-[#FF1744]/10 text-[#FF1744] border border-[#FF1744]/20 rounded flex items-center gap-1 whitespace-nowrap">
                          <Swords size={10} />
                          {isItalian ? 'Elim. Diretta' : 'Knockout'}
                        </span>
                      )}
                      {champ.tournament_type === 'groups_with_knockout' && (
                        <span className="px-2 py-0.5 text-xs bg-purple-500/10 text-purple-300 border border-purple-500/20 rounded flex items-center gap-1 whitespace-nowrap">
                          <Swords size={10} />
                          {isItalian ? 'Gironi + Elim.' : 'Groups + Knockout'}
                        </span>
                      )}
                      {(() => {
                        const total = champ.max_teams || 0;
                        const joined = champ.clubs?.length || 0;
                        const remaining = total - joined;
                        const isHot = champ.status === 'registration'
                          && total > 0
                          && remaining > 0
                          && (remaining <= 3 || remaining / total <= 0.2);
                        if (!isHot) return null;
                        return (
                          <span
                            className="badge-hot"
                            data-testid={`hot-badge-${champ.championship_id}`}
                            title={isItalian ? `Ultimi ${remaining} ${remaining === 1 ? 'posto' : 'posti'}!` : `Last ${remaining} ${remaining === 1 ? 'spot' : 'spots'}!`}
                          >
                            <Flame size={10} />
                            {isItalian
                              ? (remaining === 1 ? 'Ultimo posto' : `Ultimi ${remaining} posti`)
                              : (remaining === 1 ? 'Last spot' : `${remaining} left`)}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                  {champ.entry_fee > 0 ? (
                    <span className="px-2 sm:px-2.5 py-1 bg-[#FFEA00]/15 text-[#FFEA00] rounded text-xs font-mono whitespace-nowrap">
                      {champ.entry_fee?.toLocaleString()} <span className="hidden sm:inline">coins</span>
                    </span>
                  ) : (
                    <span className="px-2 sm:px-2.5 py-1 bg-[#00E676]/15 text-[#00E676] rounded text-xs font-bold">
                      FREE
                    </span>
                  )}
                  {expandedChamp === champ.championship_id ? (
                    <ChevronUp size={18} className="text-slate-400" />
                  ) : (
                    <ChevronDown size={18} className="text-slate-400" />
                  )}
                </div>
              </button>

              {/* Registration form (visible when champ is open for registration AND user is captain) */}
              {champ.status === 'registration' && (
                <div className="border-t border-white/10 p-4 bg-[#050508]/50">
                  {!user ? (
                    <div className="text-center text-sm text-slate-400">
                      <a href="/login" className="text-[#00F0FF] hover:underline">
                        {isItalian ? 'Accedi come capitano per iscrivere il tuo club' : 'Login as captain to register your club'}
                      </a>
                    </div>
                  ) : !isCaptain ? (
                    <p className="text-center text-xs text-slate-500">
                      {isItalian
                        ? 'Solo i capitani dei club possono iscriversi ai tornei.'
                        : 'Only club captains can register their team to tournaments.'}
                    </p>
                  ) : champ.clubs?.includes(user.club_id) ? (
                    <div className="flex items-center justify-center gap-2 text-sm text-[#00E676]" data-testid={`already-joined-${champ.championship_id}`}>
                      <Check size={14} />
                      <span>{isItalian ? 'Il tuo club è iscritto a questo torneo' : 'Your club is registered to this tournament'}</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Live progress banner */}
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-slate-400">
                          {isItalian ? 'Progresso iscrizioni' : 'Registration progress'}
                        </span>
                        <span className="font-mono text-[#FFEA00] font-bold tabular-nums">
                          {champ.clubs?.length || 0}/{champ.max_teams || 0}
                          {champ.max_teams && (champ.max_teams - (champ.clubs?.length || 0)) > 0 && (
                            <span className="text-slate-500 font-normal ml-2">
                              ({isItalian ? 'mancano' : 'missing'} {champ.max_teams - (champ.clubs?.length || 0)})
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="h-1.5 bg-[#0a0d15] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#FFEA00] to-[#00E676] transition-all duration-500"
                          style={{ width: `${champ.max_teams ? Math.min(100, ((champ.clubs?.length || 0) / champ.max_teams) * 100) : 0}%` }}
                        />
                      </div>
                      <p className="text-[10px] uppercase tracking-wider text-[#FFEA00] font-bold flex items-center gap-1 pt-1">
                        <KeyRound size={11} />
                        {isItalian ? 'Iscrivi il tuo club' : 'Register your club'}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          placeholder={isItalian ? 'Inserisci la chiave d\'accesso (es. KEY-XXXX)' : 'Enter access key'}
                          value={joinKeyByChamp[champ.championship_id] || ''}
                          onChange={(e) => setJoinKeyByChamp(prev => ({ ...prev, [champ.championship_id]: e.target.value.toUpperCase() }))}
                          className="flex-1 bg-[#0a0d15] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-[#FFEA00] focus:outline-none uppercase tracking-wider font-mono"
                          data-testid={`join-key-input-${champ.championship_id}`}
                          disabled={joiningChamp === champ.championship_id}
                        />
                        <Button
                          onClick={() => handleJoinTournament(champ)}
                          disabled={joiningChamp === champ.championship_id || !(joinKeyByChamp[champ.championship_id] || '').trim()}
                          className="bg-[#FFEA00] hover:bg-[#FFEA00]/80 text-black font-bold uppercase tracking-wider"
                          data-testid={`join-btn-${champ.championship_id}`}
                        >
                          {joiningChamp === champ.championship_id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <>
                              <KeyRound size={12} className="mr-1.5" />
                              {isItalian ? 'Iscriviti' : 'Join'}
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-[10px] text-slate-500">
                        {isItalian
                          ? 'La chiave d\'accesso ti viene fornita dall\'organizzatore del torneo.'
                          : 'The access key is provided by the tournament organizer.'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Expanded Content: Bracket (knockout) or Standings + Schedule */}
              {expandedChamp === champ.championship_id && (
                <div className="border-t border-white/10 p-3 sm:p-4 bg-[#050508]/50 space-y-4 sm:space-y-6">
                  {/* Hybrid: show knockout bracket FIRST when present */}
                  {champ.tournament_type === 'groups_with_knockout' && (
                    (() => {
                      const koMatches = (matchesData[champ.championship_id] || []).filter(m => m.phase === 'knockout');
                      if (koMatches.length === 0) {
                        return (
                          <div className="bg-[#0a0d15]/60 border border-[#FF1744]/20 rounded p-3">
                            <p className="text-[11px] uppercase tracking-wider text-[#FF1744] font-bold mb-1 flex items-center gap-1">
                              <Swords size={11} />
                              {isItalian ? 'Fase a Eliminazione Diretta' : 'Knockout Phase'}
                            </p>
                            <p className="text-xs text-slate-400">
                              {(() => {
                                const qpg = champ.qualifiers_per_group || 2;
                                const ng = champ.num_groups || 1;
                                const direct = qpg * ng;
                                const extraPos = champ.extra_qualifiers_position;
                                const extraCnt = champ.extra_qualifiers_count || 0;
                                const total = direct + (extraPos ? extraCnt : 0);
                                if (isItalian) {
                                  return extraPos
                                    ? `Verrà generata al termine della fase a gironi. Le prime ${qpg} di ogni girone (${direct} squadre dirette) + le migliori ${extraCnt} ${extraPos}ª classificate ripescate = ${total} squadre totali al tabellone.`
                                    : `Verrà generata al termine della fase a gironi. Le prime ${qpg} di ogni girone (${direct} squadre totali) accederanno al tabellone.`;
                                }
                                return extraPos
                                  ? `Generated when the group phase ends. Top ${qpg} from each group (${direct} direct) + best ${extraCnt} ${extraPos}th-placed rescued = ${total} qualifiers.`
                                  : `Will be generated when the group phase ends. Top ${qpg} from each group will qualify.`;
                              })()}
                            </p>
                          </div>
                        );
                      }
                      return (
                        <div className="bg-[#0a0d15]/60 border border-[#FF1744]/20 rounded p-3">
                          <p className="text-[11px] uppercase tracking-wider text-[#FF1744] font-bold mb-3 flex items-center gap-1">
                            <Swords size={11} />
                            {isItalian ? 'Fase a Eliminazione Diretta' : 'Knockout Phase'}
                          </p>
                          <KnockoutBracket matches={koMatches} isItalian={isItalian} shareTitle={`${champ.name}${champ.season ? ' · ' + champ.season : ''}`} />
                        </div>
                      );
                    })()
                  )}
                  {/* Pure knockout */}
                  {champ.tournament_type === 'knockout' ? (
                    <KnockoutBracket
                      matches={matchesData[champ.championship_id] || []}
                      isItalian={isItalian}
                      shareTitle={`${champ.name}${champ.season ? ' · ' + champ.season : ''}`}
                    />
                  ) : loadingStandings[champ.championship_id] ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 text-[#00F0FF] animate-spin" />
                    </div>
                  ) : standingsData[champ.championship_id] ? (
                    <>
                      {/* Multi-group format */}
                      {standingsData[champ.championship_id].type === 'multi_group' && standingsData[champ.championship_id].groups ? (
                        <div className="space-y-6">
                          {Object.entries(standingsData[champ.championship_id].groups)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([groupName, groupStandings]) => (
                              <div key={groupName} className="bg-[#0a0d15]/60 rounded p-3 border border-white/5">
                                <StandingsTable standings={groupStandings} groupName={groupName} />
                                {/* Top scorers + assistman side-by-side per girone */}
                                {(topScorersByGroup[champ.championship_id]?.[groupName]?.length > 0 ||
                                  topAssistsByGroup[champ.championship_id]?.[groupName]?.length > 0) && (
                                  <div className="mt-3 pt-3 border-t border-white/5 grid sm:grid-cols-2 gap-4">
                                    {/* Capocannonieri */}
                                    {topScorersByGroup[champ.championship_id]?.[groupName]?.length > 0 && (
                                      <div>
                                        <p className="text-[10px] uppercase tracking-wider text-[#FFD700] font-bold mb-2 flex items-center gap-1">
                                          <Trophy size={10} />
                                          {isItalian ? 'Capocannonieri Girone' : 'Top Scorers'}
                                        </p>
                                        <div className="space-y-1">
                                          {topScorersByGroup[champ.championship_id][groupName].slice(0, 5).map((p, i) => (
                                            <div key={p.player_id} className="flex items-center gap-2 text-xs">
                                              <span className={`w-5 text-right font-bold ${
                                                i === 0 ? 'text-[#FFD700]' : i === 1 ? 'text-[#C0C0C0]' : i === 2 ? 'text-[#CD7F32]' : 'text-slate-500'
                                              }`}>{i + 1}.</span>
                                              <span className="text-white truncate flex-1">{p.player_name}</span>
                                              <span className="text-slate-500 text-[10px] truncate max-w-[100px]">{p.club_name}</span>
                                              <span className="font-mono font-bold text-[#FFEA00] tabular-nums">{p.goals}⚽</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {/* Assistman */}
                                    {topAssistsByGroup[champ.championship_id]?.[groupName]?.length > 0 && (
                                      <div>
                                        <p className="text-[10px] uppercase tracking-wider text-[#00F0FF] font-bold mb-2 flex items-center gap-1">
                                          <Trophy size={10} />
                                          {isItalian ? 'Assistman Girone' : 'Top Assists'}
                                        </p>
                                        <div className="space-y-1">
                                          {topAssistsByGroup[champ.championship_id][groupName].slice(0, 5).map((p, i) => (
                                            <div key={p.player_id} className="flex items-center gap-2 text-xs">
                                              <span className={`w-5 text-right font-bold ${
                                                i === 0 ? 'text-[#00F0FF]' : i === 1 ? 'text-[#C0C0C0]' : i === 2 ? 'text-[#CD7F32]' : 'text-slate-500'
                                              }`}>{i + 1}.</span>
                                              <span className="text-white truncate flex-1">{p.player_name}</span>
                                              <span className="text-slate-500 text-[10px] truncate max-w-[100px]">{p.club_name}</span>
                                              <span className="font-mono font-bold text-[#00F0FF] tabular-nums">{p.assists}🎯</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {/* Inline schedule for this group */}
                                {matchesData[champ.championship_id] && (
                                  <ScheduleByGroup
                                    matches={matchesData[champ.championship_id]}
                                    group={groupName}
                                    isItalian={isItalian}
                                  />
                                )}
                              </div>
                            ))}
                        </div>
                      ) : (
                        <div>
                          <StandingsTable
                            standings={Array.isArray(standingsData[champ.championship_id]) ? standingsData[champ.championship_id] : []}
                            groupName={null}
                          />
                          {/* Toggle schedule for single-group tournaments */}
                          {matchesData[champ.championship_id] && matchesData[champ.championship_id].length > 0 && (
                            <div className="mt-4">
                              <button
                                onClick={() => toggleSchedule(champ.championship_id)}
                                className="text-xs text-[#00F0FF] hover:underline flex items-center gap-1"
                                data-testid={`toggle-schedule-${champ.championship_id}`}
                              >
                                <CalendarIcon size={12} />
                                {showSchedule[champ.championship_id]
                                  ? (isItalian ? 'Nascondi calendario' : 'Hide schedule')
                                  : (isItalian ? 'Mostra calendario partite' : 'Show match schedule')}
                              </button>
                              {showSchedule[champ.championship_id] && (
                                <ScheduleByGroup
                                  matches={matchesData[champ.championship_id]}
                                  group={null}
                                  isItalian={isItalian}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-center text-slate-500 py-4 text-sm">
                      {isItalian ? 'Classifica non disponibile' : 'Standings not available'}
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default Tornei;
